// Pure helpers for the rules page: grouping, wording and value display. Relative
// imports only (tests/unit/shell-policy.test.ts runs without the '@/' alias).
// Nothing here changes a value or its status: the page shows what the server holds.
import { CAPABILITY_BLOCKER_TITLES, IMPLEMENTED_ROUTES, LIFECYCLE_INFO, type PolicyLifecycle } from '../../domain/policyActivation.ts'
import type { PolicyAdminView, PolicyEvidence, PolicyView } from '../../domain/types'

export type PolicyParameter = PolicyView['parameters'][number]
export type ValueStatus = PolicyParameter['value_status']
type Tone = 'ok' | 'info' | 'synthetic' | 'bad' | 'warn'

export const VALUE_STATUS_INFO: Record<ValueStatus, { label: string; tone: Tone; explanation: string }> = {
  source_confirmed_pending_approval: {
    label: 'Pending approval',
    tone: 'info',
    explanation: 'Checked against the reference source (PDO SP-2329). Not yet adopted by BadrEOR.',
  },
  synthetic_fixture: {
    label: 'Synthetic',
    tone: 'synthetic',
    explanation: 'A demonstration-only choice so the demo can run. Not policy, and not taken from the source.',
  },
  unresolved: {
    label: 'Unresolved',
    tone: 'bad',
    explanation: 'No value has been decided. The step it governs stays blocked; the app never guesses a value.',
  },
  approved: {
    label: 'Approved',
    tone: 'ok',
    explanation: 'Adopted by BadrEOR through its approval process. Only approved values can govern real permits.',
  },
}

/** Legend order, as the statuses are introduced on the page. */
export const STATUS_ORDER: readonly ValueStatus[] = ['source_confirmed_pending_approval', 'synthetic_fixture', 'unresolved', 'approved']

export function humanise(code: string): string {
  const s = code.replace(/_/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : code
}

/** Status wording. An unknown status is never shown as approved. */
export function statusInfo(status: string): { label: string; tone: Tone; explanation: string } {
  return (
    (VALUE_STATUS_INFO as Record<string, { label: string; tone: Tone; explanation: string }>)[status] ?? {
      label: humanise(status),
      tone: 'warn',
      explanation: 'Status not recognised by this screen; treat it as not approved.',
    }
  )
}

export function statusCounts(params: readonly Pick<PolicyParameter, 'value_status'>[]): Record<string, number> {
  const counts: Record<string, number> = { source_confirmed_pending_approval: 0, synthetic_fixture: 0, unresolved: 0, approved: 0 }
  for (const p of params) counts[p.value_status] = (counts[p.value_status] ?? 0) + 1
  return counts
}

export const GROUP_TITLES: Record<string, string> = {
  permit_classes_supported: 'Permit classes',
  class_a: 'Class A permits',
  class_b: 'Class B permits',
  validation: 'Daily validation',
  holder_capacity: 'Permit Holder capacity',
  work_party: 'Work party',
  jhsep: 'Job HSE plan',
  role_routes: 'Roles and licences',
  separation: 'Role separation',
  examinations: 'Worksite examinations',
  gas: 'Gas testing',
  suspension: 'Suspension',
  supporting_documents: 'Supporting documents',
  procedure_verification: 'Procedure verification',
  routes: 'Routes not built',
  credentials: 'Credentials',
  planning: 'Planning',
  retention: 'Record retention',
  archive: 'Archive',
  timezone: 'Time zone',
}

const GROUP_ORDER = Object.keys(GROUP_TITLES)

/** "class_b.notice_min_hours" -> "class_b"; a code without a dot is its own group. */
export function groupKey(parameterCode: string): string {
  const i = parameterCode.indexOf('.')
  return i === -1 ? parameterCode : parameterCode.slice(0, i)
}

export function groupTitle(key: string): string {
  return GROUP_TITLES[key] ?? humanise(key)
}

export type ParameterGroup = { key: string; title: string; params: PolicyParameter[] }

/** Groups by prefix in a fixed reading order (unknown prefixes last, A-Z); codes A-Z within a group. */
export function groupParameters(params: readonly PolicyParameter[]): ParameterGroup[] {
  const map = new Map<string, PolicyParameter[]>()
  for (const p of params) {
    const k = groupKey(p.parameter_code)
    const list = map.get(k)
    if (list) list.push(p)
    else map.set(k, [p])
  }
  const order = (k: string) => {
    const i = GROUP_ORDER.indexOf(k)
    return i === -1 ? GROUP_ORDER.length : i
  }
  return [...map.entries()]
    .sort(([a], [b]) => order(a) - order(b) || a.localeCompare(b))
    .map(([key, list]) => ({
      key,
      title: groupTitle(key),
      params: [...list].sort((x, y) => x.parameter_code.localeCompare(y.parameter_code)),
    }))
}

/** Display text for a value: null when there is no value; pretty JSON for objects and lists. */
export function formatPolicyValue(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value, null, 2)
}

/**
 * What stays blocked while a value is unresolved, in plain words. Taken from the
 * parameter notes and the first-slice spec; anything not listed gets the general rule.
 */
const BLOCKED_WHILE_UNRESOLVED: Record<string, string> = {
  'class_a.authorisation_max_days': 'Class A permits, if this version enables them. Class B is not affected.',
  'class_a.notice_min_hours': 'Class A permits, if this version enables them. Class B is not affected.',
  'class_a.extension_allowance_days': 'Class A permits, if this version enables them. Class B is not affected.',
  'examinations.class_a': 'Class A permits, if this version enables them. Class B is not affected.',
  'gas.class_a_first_test_delegation': 'Class A permits, if this version enables them. Class B is not affected.',
  'class_b.pc_full_isolation_trigger': 'Class B work that needs full or hydrocarbon isolation (whether it needs Production Coordinator approval). The isolation itself is recorded on the application and its certificate from rules version SYN-0.5.',
  'validation.extended_route': 'Any daily validation longer than the normal validation period.',
  'holder_capacity.mixed_families': 'A Permit Holder holding Task SOP work at the same time as Class A or B permits. Task SOP is also not built.',
  'jhsep.level2_route': 'Any job that needs a Level 2 Job HSE plan, for example a work party above the Level 2 trigger.',
  'gas.numeric_limits': 'Any pass or fail of gas readings against numeric limits. The app never invents limits; here the gas tester declares each reading against the referenced method.',
  'retention.archive_policy': 'Deleting or disposing of archived permit records. Archive records show their retention as unresolved.',
  'credentials.expiry_not_stated_acceptable': 'Signing with a licence or certificate that has no stated expiry date.',
}

export const GENERAL_BLOCKED_TEXT = 'Every step that depends on this value. The app will not guess a value.'

/** For an unresolved value (or one with no value at all): what stays blocked. Otherwise null. */
export function blockedWhileUnresolved(p: Pick<PolicyParameter, 'parameter_code' | 'value_status' | 'value'>): string | null {
  if (p.value_status !== 'unresolved' && p.value !== null && p.value !== undefined) return null
  return BLOCKED_WHILE_UNRESOLVED[p.parameter_code] ?? GENERAL_BLOCKED_TEXT
}

const FAMILY_LABELS: Record<string, string> = { class_a_b: 'Class A and Class B permits' }
const RECORD_MODE_LABELS: Record<string, string> = {
  own_issue: 'BadrEOR issues the permit (own issue)',
  client_record_only: 'Client issues the permit; BadrEOR keeps a record',
}

export function permitFamilyLabel(code: string): string {
  return FAMILY_LABELS[code] ?? humanise(code)
}

/** "Class B" from permit_classes_supported, so the family label never implies Class A is built. */
export function supportedClassesText(params: readonly Pick<PolicyParameter, 'parameter_code' | 'value'>[]): string | null {
  const v = params.find((p) => p.parameter_code === 'permit_classes_supported')?.value
  if (!Array.isArray(v)) return null
  const classes = v.filter((c): c is string => typeof c === 'string')
  return classes.length > 0 ? classes.map((c) => `Class ${c}`).join(', ') : 'none'
}

export function recordModeLabel(code: string): string {
  return RECORD_MODE_LABELS[code] ?? humanise(code)
}

// -----------------------------------------------------------------------------
// Rule-set lifecycle, approval evidence and the capability check (0023). The server
// computes all of it (public.ptw_policy, public.ptw_policy_admin); these helpers word it.
// -----------------------------------------------------------------------------

/**
 * The version's lifecycle. Older servers send no lifecycle: derive the safe reading from the
 * status (an approved version is never shown as active without the server saying so).
 */
export function versionLifecycle(pv: { version: { status: string }; lifecycle?: string }): PolicyLifecycle {
  const l = pv.lifecycle
  if (l && l in LIFECYCLE_INFO) return l as PolicyLifecycle
  switch (pv.version.status) {
    case 'synthetic_fixture':
      return 'synthetic'
    case 'withdrawn':
      return 'withdrawn'
    case 'approved':
      return 'approved'
    default:
      return 'candidate'
  }
}

export function lifecycleInfo(l: PolicyLifecycle) {
  return LIFECYCLE_INFO[l]
}

export function blockerTitle(code: string): string {
  return CAPABILITY_BLOCKER_TITLES[code] ?? humanise(code.toLowerCase())
}

type ApprovalRecord = PolicyEvidence['approvals'][number]

/** "Reviewed by Name (HSE Manager) on 2026-11-09 - DOC Rev A". Names come only from the recorded evidence. */
export function approvalSentence(a: Pick<ApprovalRecord, 'approval_kind' | 'approver_name' | 'approver_role_title' | 'approved_on' | 'document_reference' | 'document_revision'>): string {
  const verb = a.approval_kind === 'review' ? 'Reviewed' : 'Approved'
  return `${verb} by ${a.approver_name} (${a.approver_role_title}) on ${a.approved_on} - ${a.document_reference} Rev ${a.document_revision}`
}

const IMPLEMENTED_ROUTE_CODES = IMPLEMENTED_ROUTES.map((r) => r.code)

/**
 * routes.unsupported of SYN-0.1..0.3 (frozen before 0020) still lists 'extension'. The route is
 * built: say so next to the value, with the allowance that gates it. Null when nothing to explain.
 */
export function implementedRouteNote(
  params: readonly Pick<PolicyParameter, 'parameter_code' | 'value'>[],
  implemented: readonly string[] = IMPLEMENTED_ROUTE_CODES,
): string | null {
  const listed = params.find((p) => p.parameter_code === 'routes.unsupported')?.value
  if (!Array.isArray(listed)) return null
  const built = listed.filter((r): r is string => typeof r === 'string' && implemented.includes(r))
  if (built.length === 0) return null
  const parts: string[] = []
  if (built.includes('extension')) {
    const allowance = params.find((p) => p.parameter_code === 'class_b.extension_allowance_days')?.value
    const days = typeof allowance === 'number' ? `${allowance} ${allowance === 1 ? 'day' : 'days'}` : 'the class extension allowance'
    parts.push(
      `Extension is listed here because this version was fixed before extensions were built. Extensions are built: the PA or PH asks before the authorised end, the RS approves or refuses, and the total added after the original end is at most ${days} (class_b.extension_allowance_days). No Operations Manager step: the OM approval carries over.`,
    )
  }
  const others = built.filter((r) => r !== 'extension')
  if (others.length > 0) parts.push(`Also built although listed here: ${others.join(', ')}.`)
  return parts.join(' ')
}

/** A frozen note that the software has overtaken (the SYN-0.1..0.3 extension allowance note). */
export function supersededNote(p: Pick<PolicyParameter, 'parameter_code' | 'note'>): string | null {
  if (p.parameter_code === 'class_b.extension_allowance_days' && p.note && /not built/i.test(p.note)) {
    return 'The extension command is built now: this value limits RS-approved extensions in total after the original authorised end.'
  }
  return null
}

export type AdminVersion = PolicyAdminView['versions'][number]
export type AdminAction = 'edit' | 'freeze' | 'record_review' | 'record_approval' | 'activate' | 'withdraw'

/** Which administration steps a version offers now, and why the others are not offered. */
export function adminActions(v: Pick<AdminVersion, 'lifecycle' | 'profile_is_synthetic' | 'approvals' | 'activation_blockers'>, environmentKind: string): {
  available: AdminAction[]
  notes: string[]
} {
  const available: AdminAction[] = []
  const notes: string[] = []
  if (v.profile_is_synthetic || v.lifecycle === 'synthetic') {
    notes.push('Synthetic demonstration versions are never edited, approved or activated.')
    return { available, notes }
  }
  if (v.lifecycle === 'candidate') available.push('edit', 'freeze')
  if (v.lifecycle === 'frozen') {
    available.push('record_review')
    const reviewed = v.approvals.some((a) => a.approval_kind === 'review')
    if (environmentKind !== 'operational') notes.push('Approvals are recorded only in the operational environment.')
    else if (!reviewed) notes.push('Record the review before the approval.')
    else available.push('record_approval')
  }
  if (['approved', 'scheduled', 'active', 'superseded'].includes(v.lifecycle)) {
    const versionLevel = v.activation_blockers.filter((b) => !['AUTHORITY_REQUIRED', 'ALREADY_ACTIVE', 'LATER_ACTIVATION_SCHEDULED'].includes(b.code))
    if (versionLevel.length === 0) available.push('activate')
    else notes.push('Activation is refused until every blocker below is cleared.')
    available.push('withdraw')
  }
  return { available, notes }
}

/** Value text typed by an administrator: JSON when it parses, else the plain text. */
export function parseValueInput(text: string): unknown {
  const s = text.trim()
  if (s === '') return null
  try {
    return JSON.parse(s)
  } catch {
    return s
  }
}
